// Adicionar fotografias, sem sair do álbum.
//
// Era metade da página /adicionar (a outra metade era o marco, numa aba ao
// lado). Agora é um formulário só dela, aberto no próprio álbum — como o mapa
// e o calendário fazem com os lugares e os eventos.
//
// O envio continua igual: sign -> PUT direto ao R2 -> confirm, três a três
// (STACK.md §9); os bytes nunca passam pela API.

import { useState } from "react";
import { longDate as dataLonga } from "@historia/shared/dates";
import { AFTER_PHOTO_CHANGE, createPhoto } from "@/api/mutations";
import { useAccao } from "@/api/useAccao";
import { checkFiles, uploadBatch } from "@/lib/uploadFile";

type MarcoResumo = { id: string; titulo: string; data: string };
type AlbumResumo = { id: string; nome: string };

const campo =
  "rounded-xl border border-ouro/30 bg-creme-50 px-3 py-2 text-sm text-tinta outline-none " +
  "placeholder:text-tinta-700/40 focus:border-ouro focus:ring-2 focus:ring-ouro/30";
const etiqueta = "text-xs font-semibold text-tinta-700";

export function FormFoto({
  marcos,
  albuns,
  albumInicial,
  aoTerminar,
}: {
  marcos: MarcoResumo[];
  albuns: AlbumResumo[];
  /** Pasta aberta no álbum — fica pré-escolhida, que é quase sempre o que se quer. */
  albumInicial?: string;
  aoTerminar: () => void;
}) {
  const [, registar] = useAccao(createPhoto, { invalidate: AFTER_PHOTO_CHANGE });

  const [ficheiros, setFicheiros] = useState<File[]>([]);
  const [legenda, setLegenda] = useState("");
  const [marcoId, setMarcoId] = useState("");
  const [albumId, setAlbumId] = useState(albumInicial ?? "");
  const [erro, setErro] = useState<string | null>(null);
  const [aEnviar, setAEnviar] = useState(false);
  const [progresso, setProgresso] = useState({ feitas: 0, total: 0 });

  function aoEscolher(e: React.ChangeEvent<HTMLInputElement>) {
    setErro(null);
    setFicheiros(Array.from(e.target.files ?? []));
  }

  function removerFicheiro(indice: number) {
    setFicheiros((atual) => atual.filter((_, i) => i !== indice));
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);

    if (ficheiros.length === 0) {
      setErro("Escolhe pelo menos uma imagem.");
      return;
    }

    // Validação ficheiro a ficheiro: separa as boas das más em vez de bloquear
    // o lote inteiro. Os limites vêm de @historia/shared, por isso não podem
    // divergir do que a API impõe.
    const { valid: validos, rejected: invalidos } = checkFiles(ficheiros, "foto");

    if (validos.length === 0) {
      setErro(`Nenhuma imagem válida. Ignoradas: ${invalidos.join(", ")}`);
      return;
    }

    setAEnviar(true);
    setProgresso({ feitas: 0, total: validos.length });

    try {
      const { mediaIds, failures } = await uploadBatch(validos, "foto", (feitas, total) =>
        setProgresso({ feitas, total }),
      );

      // Cada envio confirmado vira uma linha de foto.
      const falhas = [...failures];
      for (const mediaId of mediaIds) {
        const resultado = await registar({
          mediaId,
          legenda,
          marcoId: marcoId || null,
          albumId: albumId || null,
        });
        if (!resultado.ok) falhas.push(resultado.erro ?? "não ficou registada");
      }

      const problemas = [...invalidos, ...falhas];

      // Correu tudo bem → fecha o formulário; a grelha já foi invalidada.
      if (problemas.length === 0) {
        aoTerminar();
        return; // mantém desativado enquanto desmonta
      }

      // Algumas falharam: mostra o que correu mal; o resto já ficou guardado.
      const enviadas = validos.length - falhas.length;
      setErro(
        `Enviadas ${enviadas} de ${ficheiros.length}. Com problemas: ${problemas.join(", ")}.`,
      );
      setAEnviar(false);
    } catch {
      setErro("Algo correu mal ao enviar. Tenta novamente.");
      setAEnviar(false);
    }
  }

  return (
    <form
      onSubmit={enviar}
      className="flex flex-col gap-3 rounded-2xl border border-ouro/25 bg-creme-50 p-4 text-left"
    >
      <h3 className="font-display text-lg text-tinta">Novas fotografias</h3>

      <label className="flex flex-col gap-1">
        <span className={etiqueta}>Imagens</span>
        <input
          type="file"
          // image/* abre logo a galeria no telemóvel (uma lista de MIME fixa fazia
          // o iOS abrir os Ficheiros em vez das Fotos). A validação por tipo é feita
          // a seguir no envio, por isso continua seguro.
          accept="image/*"
          multiple
          required={ficheiros.length === 0}
          onChange={aoEscolher}
          className="rounded-xl border border-ouro/30 bg-creme-50 px-3 py-2 text-sm text-tinta-700 file:mr-3 file:rounded-full file:border-0 file:bg-ouro/15 file:px-4 file:py-1.5 file:text-sm file:font-semibold file:text-ouro-600 hover:file:bg-ouro/25"
        />
        <span className="text-xs text-tinta-700/60">
          Podes escolher várias · JPG, PNG, WEBP ou GIF · até 10 MB cada
        </span>
      </label>

      {ficheiros.length > 0 && (
        <ul className="flex flex-col gap-1 rounded-xl border border-ouro/15 bg-creme-50/60 p-3">
          <li className="mb-1 text-xs font-semibold text-tinta-700">
            {ficheiros.length}{" "}
            {ficheiros.length === 1 ? "imagem selecionada" : "imagens selecionadas"}
          </li>
          {ficheiros.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex items-center justify-between gap-2 text-sm text-tinta-700"
            >
              <span className="truncate">{f.name}</span>
              {!aEnviar && (
                <button
                  type="button"
                  onClick={() => removerFicheiro(i)}
                  aria-label={`Remover ${f.name}`}
                  className="shrink-0 rounded-full px-2 text-tinta-700/60 transition-colors hover:bg-creme-200 hover:text-tinta"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <label className="flex flex-col gap-1">
        <span className={etiqueta}>Legenda (opcional — aplica-se a todas)</span>
        <input
          value={legenda}
          onChange={(e) => setLegenda(e.target.value)}
          placeholder="Aquele dia perfeito…"
          className={campo}
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className={etiqueta}>Ligar a um marco (opcional)</span>
          <select value={marcoId} onChange={(e) => setMarcoId(e.target.value)} className={campo}>
            <option value="">— Só no álbum —</option>
            {marcos.map((m) => (
              <option key={m.id} value={m.id}>
                {m.titulo} ({dataLonga(m.data)})
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className={etiqueta}>Pasta / álbum (opcional)</span>
          <select value={albumId} onChange={(e) => setAlbumId(e.target.value)} className={campo}>
            <option value="">— Sem pasta (álbum geral) —</option>
            {albuns.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nome}
              </option>
            ))}
          </select>
        </label>
      </div>

      {erro && (
        <p
          role="alert"
          className="rounded-2xl border border-blush/60 bg-blush-300/30 px-4 py-2.5 text-sm text-tinta"
        >
          {erro}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={aoTerminar}
          disabled={aEnviar}
          className="rounded-full px-4 py-1.5 text-sm font-semibold text-tinta-700 transition-colors hover:bg-creme-200 disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={aEnviar}
          className="rounded-full bg-ouro px-5 py-1.5 text-sm font-semibold text-creme-50 transition-colors hover:bg-ouro-600 disabled:opacity-60"
        >
          {aEnviar
            ? `A enviar… ${progresso.feitas}/${progresso.total}`
            : ficheiros.length > 1
              ? `Guardar ${ficheiros.length} fotos`
              : "Guardar foto"}
        </button>
      </div>
    </form>
  );
}
